// Unified Logging System for CAPIVIO Platform
// Ensures consistent logging across all services and components
// Supports compliance, audit, and debugging requirements

import { db } from "../services/firebase";
import { doc, collection, setDoc, serverTimestamp } from "firebase/firestore";

// Log levels for different types of messages
const LOG_LEVELS = {
    DEBUG: "debug",
    INFO: "info", 
    WARN: "warn",
    ERROR: "error",
    AUDIT: "audit",
    COMPLIANCE: "compliance"
};

// Log categories for better organization
const LOG_CATEGORIES = {
    AUTH: "authentication",
    KYC: "kyc",
    INVESTMENT: "investment",
    OPPORTUNITY: "opportunity",
    ADMIN: "admin",
    USER: "user",
    SYSTEM: "system",
    COMPLIANCE: "compliance",
    AUDIT: "audit",
    SERIALIZATION: "serialization"
};

class Logger {
    constructor() {
        this.isDevelopment = process.env.NODE_ENV === "development";
        this.isProduction = process.env.NODE_ENV === "production";
    }

    // Sanitize sensitive data before logging
    sanitizeData(data) {
        if (!data) return data;
        
        const sensitiveFields = [
            "password", "token", "secret", "key", "dni", "passportNumber",
            "creditCard", "bankAccount", "ssn", "personalId"
        ];
        
        const sanitized = { ...data };
        sensitiveFields.forEach(field => {
            if (sanitized[field]) {
                sanitized[field] = "[REDACTED]";
            }
        });
        
        return sanitized;
    }

    // Format log message consistently
    formatMessage(level, category, message, data = null) {
        const timestamp = new Date().toISOString();
        const logEntry = {
            timestamp,
            level,
            category,
            message,
            environment: process.env.NODE_ENV,
            version: process.env.REACT_APP_VERSION || "1.0.0"
        };

        if (data) {
            logEntry.data = this.sanitizeData(data);
        }

        return logEntry;
    }

    // Console logging for development
    consoleLog(level, category, message, data = null) {
        if (!this.isDevelopment) return;

        const prefix = `[${level.toUpperCase()}] [${category.toUpperCase()}]`;
        
        switch (level) {
        case LOG_LEVELS.ERROR:
            // eslint-disable-next-line no-console
            console.error(prefix, message, data ? this.sanitizeData(data) : "");
            break;
        case LOG_LEVELS.WARN:
            // eslint-disable-next-line no-console
            console.warn(prefix, message, data ? this.sanitizeData(data) : "");
            break;
        case LOG_LEVELS.DEBUG:
            // eslint-disable-next-line no-console
            console.debug(prefix, message, data ? this.sanitizeData(data) : "");
            break;
        default:
            // eslint-disable-next-line no-console
            console.log(prefix, message, data ? this.sanitizeData(data) : "");
        }
    }

    // Firestore logging for production and compliance
    async firestoreLog(level, category, message, data = null, userId = null) {
        if (!this.isProduction && level !== LOG_LEVELS.AUDIT && level !== LOG_LEVELS.COMPLIANCE) {
            return;
        }

        try {
            const logEntry = this.formatMessage(level, category, message, data);
            logEntry.userId = userId;
            logEntry.serverTimestamp = serverTimestamp();

            // Determine collection based on log level
            let collectionName = "application_logs";
            if (level === LOG_LEVELS.AUDIT) {
                collectionName = "audit_logs";
            } else if (level === LOG_LEVELS.COMPLIANCE) {
                collectionName = "compliance_logs";
            } else if (level === LOG_LEVELS.ERROR) {
                collectionName = "error_logs";
            }

            const logRef = doc(collection(db, collectionName));
            await setDoc(logRef, logEntry);
        } catch (error) {
            // Fallback to console if Firestore logging fails
            // eslint-disable-next-line no-console
            console.error("Failed to log to Firestore:", error);
            this.consoleLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.SYSTEM, "Firestore logging failed", { error: error.message });
        }
    }

    // Public logging methods
    async log(level, category, message, data = null, userId = null) {
        this.consoleLog(level, category, message, data);
        await this.firestoreLog(level, category, message, data, userId);
    }

    // Convenience methods for different log levels
    async debug(category, message, data = null, userId = null) {
        await this.log(LOG_LEVELS.DEBUG, category, message, data, userId);
    }

    async info(category, message, data = null, userId = null) {
        await this.log(LOG_LEVELS.INFO, category, message, data, userId);
    }

    async warn(category, message, data = null, userId = null) {
        await this.log(LOG_LEVELS.WARN, category, message, data, userId);
    }

    async error(category, message, error = null, userId = null) {
        const errorData = error ? {
            message: error.message,
            stack: error.stack,
            name: error.name
        } : null;
        await this.log(LOG_LEVELS.ERROR, category, message, errorData, userId);
    }

    // Specialized methods for compliance and audit
    async audit(action, details = {}, userId = null) {
        await this.log(LOG_LEVELS.AUDIT, LOG_CATEGORIES.AUDIT, action, details, userId);
    }

    async compliance(event, details = {}, userId = null) {
        await this.log(LOG_LEVELS.COMPLIANCE, LOG_CATEGORIES.COMPLIANCE, event, details, userId);
    }

    // User action logging
    async userAction(userId, action, details = {}) {
        await this.audit(`user_action: ${action}`, { userId, ...details }, userId);
    }

    // Admin action logging
    async adminAction(adminId, action, targetUserId = null, details = {}) {
        await this.audit(`admin_action: ${action}`, { 
            adminId, 
            targetUserId, 
            ...details 
        }, adminId);
    }

    // KYC event logging
    async kycEvent(userId, event, details = {}) {
        await this.compliance(`kyc_${event}`, { userId, ...details }, userId);
    }

    // Investment event logging
    async investmentEvent(userId, event, details = {}) {
        await this.audit(`investment_${event}`, { userId, ...details }, userId);
    }

    // Opportunity event logging
    async opportunityEvent(userId, event, opportunityId = null, details = {}) {
        await this.audit(`opportunity_${event}`, { 
            userId, 
            opportunityId, 
            ...details 
        }, userId);
    }
}

// Export singleton instance
export const logger = new Logger();

// Export constants for use in other files
export { LOG_LEVELS, LOG_CATEGORIES }; 